import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { locationsApi } from 'api/locations'
import {
  AmountType,
  OrderHandoff,
  ServiceChargeDivergence,
  useGetServiceChargeGroupsQuery,
} from 'api/serviceCharges'
import PageHeader from 'components/PageHeader'
import TablePaginationFooter, {
  DEFAULT_PAGE_SIZE,
  PageSize,
} from 'components/TablePaginationFooter'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useMemo, useState } from 'react'
import { Container } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import { Link, type RouteComponentProps } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'
import { badgeBaseClass } from '../LocationsTab/helpers'
import {
  tableBodyCellClass,
  tableHeaderCellClass,
  tableRowHoverClass,
  tableShellClass,
} from '../tableStyles'
import FilterBar, { ServiceChargeGroupFilters } from './FilterBar'

const t = buildTranslate('locations.location_list.service_charge_groups')

const SEARCH_DEBOUNCE_MS = 300

const EMPTY_FILTERS: ServiceChargeGroupFilters = {
  types: [],
  enabled: undefined,
  handoffs: [],
  locationIds: [],
}

const hasActiveFilters = (
  filters: ServiceChargeGroupFilters,
  search: string
): boolean =>
  search !== '' ||
  filters.types.length > 0 ||
  filters.enabled !== undefined ||
  filters.handoffs.length > 0 ||
  filters.locationIds.length > 0

// Reads exactly the flags the wire sends — never widens or recomputes them.
// A group with an empty/all-false divergence object renders as consistent,
// regardless of what any underlying per-location data might suggest.
const isDivergent = (divergence: ServiceChargeDivergence): boolean =>
  Boolean(
    divergence.amount || divergence.criteria || divergence.criteria_override
  )

const DivergenceBadge = ({
  divergence,
}: {
  divergence: ServiceChargeDivergence
}) => {
  const [css] = useStyletron()
  const divergent = isDivergent(divergence)
  return (
    <span
      className={`${badgeBaseClass(css)} ${css({
        backgroundColor: divergent ? palette.eggYolk10 : palette.grey20,
        color: divergent ? palette.eggYolk50 : palette.grey70,
      })}`}
    >
      {divergent
        ? (t('divergence_badge.divergent') as string)
        : (t('divergence_badge.consistent') as string)}
    </span>
  )
}

type LocationState = { fromLocationId?: number } | undefined

const ServiceChargeGroupsList = ({
  location,
}: RouteComponentProps<{}, {}, LocationState>) => {
  const [css] = useStyletron()
  const backPath = location.state?.fromLocationId
    ? `/locations/${location.state.fromLocationId}`
    : '/locations'
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [filters, setFilters] =
    useState<ServiceChargeGroupFilters>(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE)

  const {
    data: locationsData,
    isError: isLocationsError,
    isLoading: isLocationsLoading,
  } = locationsApi.useGetLocationsQuery({})
  const locationOptions = useMemo(
    () =>
      (locationsData?.locations ?? []).map(location => ({
        id: location.id,
        name: location.name ?? '',
      })),
    [locationsData]
  )

  const { data, isLoading, isFetching, isError } =
    useGetServiceChargeGroupsQuery({
      types: filters.types,
      search: search || undefined,
      enabled: filters.enabled,
      handoffs: filters.handoffs,
      locationIds: filters.locationIds,
      page,
      perPage: pageSize,
    })

  const groups = data?.data ?? []

  const totalCount = data?.meta?.total_count ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const [debouncedSetSearch, cancelDebouncedSearch] = useDebouncedCallback(
    (value: string) => {
      setSearch(value)
      setPage(1)
    },
    SEARCH_DEBOUNCE_MS
  )
  useEffect(() => cancelDebouncedSearch, [cancelDebouncedSearch])

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSetSearch(value)
  }

  const updateFilters = (next: Partial<ServiceChargeGroupFilters>) => {
    setFilters(prev => ({ ...prev, ...next }))
    setPage(1)
  }

  const handleClearFilters = () => {
    cancelDebouncedSearch()
    setSearchInput('')
    setSearch('')
    setFilters(EMPTY_FILTERS)
    setPage(1)
  }

  const handlePageSizeChange = (size: PageSize) => {
    setPageSize(size)
    setPage(1)
  }

  const filtersActive = hasActiveFilters(filters, search)

  const emptyMessageKey = (): string => {
    if (locationOptions.length === 0) return 'empty_no_accessible_locations'
    return filtersActive ? 'empty_filtered' : 'empty_no_filters'
  }

  const renderBody = (): React.ReactNode => {
    if (isLoading || isLocationsLoading) {
      return (
        <p
          role="status"
          className={`body-text-3 ${css({ color: palette.grey70 })}`}
        >
          {t('loading')}
        </p>
      )
    }
    if (isError) {
      return (
        <div data-testid="service-charge-groups-fetch-error" role="alert">
          <Notification kind="negative" size="full">
            <Text.BodyText4 color="grey70" className="p-xs" tag="div">
              {t('fetch_error')}
            </Text.BodyText4>
          </Notification>
        </div>
      )
    }
    if (isLocationsError) {
      return (
        <div data-testid="service-charge-groups-locations-error" role="alert">
          <Notification kind="negative" size="full">
            <Text.BodyText4 color="grey70" className="p-xs" tag="div">
              {t('locations_fetch_error')}
            </Text.BodyText4>
          </Notification>
        </div>
      )
    }
    if (groups.length === 0) {
      return (
        <div className="text-center py-xl" role="status">
          <Text.BodyText3 color="grey70">{t(emptyMessageKey())}</Text.BodyText3>
          {locationOptions.length > 0 && filtersActive && (
            <div className="mt-m">
              <button
                type="button"
                className={`btn btn-link ${css({ color: palette.belizeHole })}`}
                onClick={handleClearFilters}
              >
                {t('clear_filters')}
              </button>
            </div>
          )}
        </div>
      )
    }
    return (
      <>
        <table
          className={`full-width ${tableShellClass(css)}`}
          role="table"
          data-testid="service-charge-groups-table"
        >
          <thead>
            <tr>
              <th scope="col" className={tableHeaderCellClass(css)}>
                {t('table.name')}
              </th>
              <th scope="col" className={tableHeaderCellClass(css)}>
                {t('table.amount_type')}
              </th>
              <th scope="col" className={tableHeaderCellClass(css)}>
                {t('table.locations')}
              </th>
              <th scope="col" className={tableHeaderCellClass(css)}>
                {t('table.enabled')}
              </th>
              <th scope="col" className={tableHeaderCellClass(css)}>
                {t('table.override_delivery')}
              </th>
              <th scope="col" className={tableHeaderCellClass(css)}>
                {t('table.override_takeout')}
              </th>
              <th scope="col" className={tableHeaderCellClass(css)}>
                {t('table.divergence')}
              </th>
              <th
                scope="col"
                className={tableHeaderCellClass(css)}
                aria-label={t('table.view_header') as string}
              />
            </tr>
          </thead>
          <tbody>
            {groups.map((group, index) => {
              const isLastRow = index === groups.length - 1
              const cellClassName = tableBodyCellClass(css, isLastRow)
              return (
                <tr
                  key={group.provider_uid}
                  className={tableRowHoverClass(css)}
                >
                  <td className={cellClassName}>
                    <div className="d-flex align-items-center">
                      <Text.BodyText4 bold color="grey90" className="mr-xs">
                        {group.name}
                      </Text.BodyText4>
                      {group.recommended_for_delivery && (
                        <span
                          className={`${badgeBaseClass(css)} ${css({
                            backgroundColor: palette.belizeHole10,
                            color: palette.belizeHole,
                          })}`}
                        >
                          {t('recommended_badge')}
                        </span>
                      )}
                    </div>
                    <Text.BodyText5 color="grey60">
                      {t('provider_uid_label', { uid: group.provider_uid })}
                    </Text.BodyText5>
                  </td>
                  <td className={cellClassName}>
                    {t(
                      `filters.type_options.${group.amount_type.toLowerCase()}`
                    )}
                  </td>
                  <td className={cellClassName}>{group.location_count}</td>
                  <td className={cellClassName}>{group.enabled_count}</td>
                  <td className={cellClassName}>
                    {group.override_delivery_count}
                  </td>
                  <td className={cellClassName}>
                    {group.override_takeout_count}
                  </td>
                  <td className={cellClassName}>
                    <DivergenceBadge divergence={group.divergence} />
                  </td>
                  <td className={cellClassName}>
                    <Link
                      to={{
                        pathname: `/service_charges/${encodeURIComponent(
                          group.provider_uid
                        )}`,
                        state: { groupName: group.name },
                      }}
                      aria-label={
                        t('table.view_aria', { name: group.name }) as string
                      }
                    >
                      {t('table.view_header')}
                    </Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {isFetching && !isLoading && (
          <span className="sr-only" role="status" aria-live="polite">
            {t('loading')}
          </span>
        )}
        <TablePaginationFooter
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </>
    )
  }

  return (
    <PageHeader
      title={t('title')}
      description={t('description')}
      backPath={backPath}
    >
      <Helmet>
        <title>{t('page_title')}</title>
      </Helmet>
      <Container>
        <FilterBar
          searchValue={searchInput}
          onSearchChange={handleSearchChange}
          filters={filters}
          onTypesChange={(types: AmountType[]) => updateFilters({ types })}
          onEnabledChange={(enabled: boolean | undefined) =>
            updateFilters({ enabled })
          }
          onHandoffsChange={(handoffs: OrderHandoff[]) =>
            updateFilters({ handoffs })
          }
          locations={locationOptions}
          onToggleLocation={id =>
            updateFilters({
              locationIds: filters.locationIds.includes(id)
                ? filters.locationIds.filter(existing => existing !== id)
                : [...filters.locationIds, id],
            })
          }
        />
        {renderBody()}
      </Container>
    </PageHeader>
  )
}

export default ServiceChargeGroupsList
